import { logger } from '@/shared/utils';
import { useEffect, useState } from 'react';
import { DefaultProfilePicture } from '@/assets/images';
import { getMediaURL } from '@/infrastructure/sdk';
import { profileImageStorage } from '@/features/indexedDB';

const cache = {};
const pendingCache = {};

const generateKey = (imageUri, userId) => {
   if (typeof imageUri === 'string') return `${userId}-${imageUri}`;
   if (imageUri instanceof Blob) return `${userId}-blob-${imageUri.size}-${imageUri.type}`;
   return `${userId}-unknown`;
};

/**
 * Pure utility to resolve an image URI (token, blob, data-uri, etc.) into a usable source.
 * This can be used outside of React components.
 */
export const resolveProfileImage = async (imageUri, userId, defaultImg = DefaultProfilePicture) => {
   if (!imageUri) return defaultImg;

   const key = generateKey(imageUri, userId);

   try {
      // 1. Cache hit
      if (cache[key]) {
         return cache[key];
      }

      // 2. IndexedDB
      const stored = await profileImageStorage.get(key);
      if (stored?.blob) {
         const blobUrl = URL.createObjectURL(stored.blob);
         cache[`blobUrl-${key}`] = blobUrl;
         cache[key] = blobUrl;
         return blobUrl;
      }

      // 3. Simple strings (data, http, blob)
      if (typeof imageUri === 'string') {
         if (imageUri.startsWith('data:') || imageUri.startsWith('http') || imageUri.startsWith('blob:')) {
            cache[key] = imageUri;
            return imageUri;
         }
      }

      // 4. Blob instance
      if (imageUri instanceof Blob) {
         if (imageUri.size > 0 && imageUri.type.startsWith('image/')) {
            const blobUrl = URL.createObjectURL(imageUri);
            cache[`blobUrl-${key}`] = blobUrl;
            cache[key] = blobUrl;
            await profileImageStorage.save(key, imageUri);
            return blobUrl;
         }
         return defaultImg;
      }

      // 5. Fetch from SDK
      if (typeof imageUri === 'string' && imageUri.trim() !== '' && imageUri !== '[object Blob]') {
         if (pendingCache[key]) {
            return await pendingCache[key];
         }

         const fetchPromise = getMediaURL(imageUri)
            .then(res => {
               if (res.statusCode === 200 && res.data?.blob) {
                  const blob = res.data.blob;
                  if (blob.size > 0 && blob.type.startsWith('image/')) {
                     const blobUrl = URL.createObjectURL(blob);
                     cache[`blobUrl-${key}`] = blobUrl;
                     cache[key] = blobUrl;
                     profileImageStorage.save(key, blob);
                     return blobUrl;
                  }
               }
               if (res.statusCode === 200 && res.data?.blobUrl) {
                  cache[key] = res.data.blobUrl;
                  return res.data.blobUrl;
               }
               return defaultImg;
            })
            .catch(() => defaultImg)
            .finally(() => {
               delete pendingCache[key];
            });

         pendingCache[key] = fetchPromise;
         return await fetchPromise;
      }
   } catch (err) {
      logger.error('resolveProfileImage error:', err);
   }

   return defaultImg;
};

/**
 * React hook that uses the resolveProfileImage utility.
 */
export const useProfileImage = ({ imageUri = '', userId = '', defaultImg = DefaultProfilePicture, setImageSource }) => {
   const key = generateKey(imageUri, userId);

   const [src, setSrc] = useState(() => cache[key] || (typeof defaultImg === 'string' ? defaultImg : ''));
   const [loading, setLoading] = useState(() => !!imageUri && !cache[key]);

   useEffect(() => {
      let isMounted = true;

      const cleanupBlob = () => {
         const prevKey = cache[`prevKey-${userId}`];
         if (prevKey && prevKey !== key) {
            const blobUrl = cache[`blobUrl-${prevKey}`];
            if (blobUrl) {
               URL.revokeObjectURL(blobUrl);
               delete cache[`blobUrl-${prevKey}`];
            }
         }
         cache[`prevKey-${userId}`] = key;
      };

      const handleResolve = async () => {
         setLoading(true);
         cleanupBlob();

         const result = await resolveProfileImage(imageUri, userId, defaultImg);
         
         if (isMounted) {
            setSrc(result);
            setImageSource?.(result);
            setLoading(false);
         }
      };

      handleResolve();
      return () => {
         isMounted = false;
      };
   }, [key, imageUri, userId, defaultImg, setImageSource]);

   return { src, loading };
};
